import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { NavigateFunction } from "react-router";

import { Badge, Color, Icon } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { CaseListResult } from "capitalrx-types";
import { MultiSelectDropdown } from "capitalrx-components-2.0/lib/forms";

import { MedicalClaimListObject } from "@/App/api/claims";
import { MemberClaim } from "@/App/api/member";
import { RecentActivityTypes } from "@/App/constants/constants";
import { formatDollars, getClaimProviderName } from "@/App/utils/utils";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { useIsDentalUser, useIsPharmacyUser, useIsUnifiedUser, useIsVisionUser, useNavigateClient } from "@/App/hooks";

import { ActivityCard } from "./ActivityCard";
import * as FAKE_DATA from "./fake_data.json";

const TextMultiSelectDropdown = styled(MultiSelectDropdown)`
  &:focus-visible {
    outline: 0!important;
  }
  div[role="combobox"] {
    @media screen and (min-width: 768px) {
      min-width: 175px;
    }
    
    & > .control {
      border: none;

      & > span {
        display: none;
      }

      &.focused {
        box-shadow: initial;
      }

      & .value-container > div {
        font-size: 15px;
        line-height: 20px;
        color: ${Color.toString(Color.Turqoise["500"])}};
        font-weight: 600;
      }
    }
  }
  & .badge-container, & .clear-container {
    display: none!important;
  }
`;

const CLAIMS_PAGE_TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard";

const ActivityBadge = ({ activity, index, handleBadgeClose }: {activity: RecentActivityTypes, index: number, handleBadgeClose: (index: number) => void}) => {
  const { t: translate } = useTranslation();

  if (activity === "prior_authorization") {
    return <Badge type="system-alternative" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.priorAuthorization`)}</Badge>;
  } else if (activity === "medical") {
    return <Badge type="success" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.medicalClaim`)}</Badge>;
  } else if (activity === "pharmacy") {
    return <Badge type="system" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.pharmacyClaim`)}</Badge>;
  } else if (activity === "dental") {
    return <Badge type="info" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.dentalClaim`)}</Badge>;
  } else {
    return <Badge type="info" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.visionClaim`)}</Badge>;
  }
};

export type ActivityTypes = { readonly activity_date: string; readonly title: string, readonly description: JSX.Element | string, readonly link: () => NavigateFunction } &
( { readonly medical_claim: MedicalClaimListObject; readonly claim?: never; readonly pa_case?: never }
  | { readonly medical_claim?: never; readonly claim: MemberClaim; readonly pa_case?: never }
  | { readonly medical_claim?: never; readonly claim?: never; readonly pa_case: CaseListResult["case"] });

type CombinedActivityTypes = Array<ActivityTypes>;

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard";

export const RecentActivity = () => {
  const { t: translate } = useTranslation();
  const [claimsType, setClaimsType] = useState<RecentActivityTypes[]>();
  const navigate = useNavigateClient();
  const { isUnifiedUser } = useIsUnifiedUser();
  const { isDentalUser } = useIsDentalUser();
  const { isVisionUser } = useIsVisionUser();
  const { isPharmacyUser } = useIsPharmacyUser();
  const hasMedicalBenefits = isUnifiedUser;

  const claimFilterTypes = [
    { type: "medical", value: translate(`${TRANSLATION_PATH}.medicalClaim`) },
    { type: "pharmacy", value: translate(`${TRANSLATION_PATH}.pharmacyClaim`) },
    { type: "vision", value: translate(`${TRANSLATION_PATH}.visionClaim`) },
    { type: "dental", value: translate(`${TRANSLATION_PATH}.dentalClaim`) },
    { type: "prior_authorization", value: translate(`${TRANSLATION_PATH}.priorAuthorization`) },
  ];

  const availableFilters = claimFilterTypes.filter(({ type }) => {
    if (type === "pharmacy") {
      return isPharmacyUser;
    }
    if (type === "medical") {
      return hasMedicalBenefits;
    }
    if (type === "dental") {
      return isDentalUser;
    }
    if (type === "vision") {
      return isVisionUser;
    }
    return true;
  });


  let dataResults = FAKE_DATA.results;
  let combinedActivity: CombinedActivityTypes = [];

  dataResults = FAKE_DATA.results.filter((data) => {
    const hasNoFilter = !claimsType || claimsType.length === 0;
    const dataIsMedicalClaimsType = Object.hasOwn(data, "medical_claim");

    const shouldShowPharmacy = isPharmacyUser && (hasNoFilter || claimsType.includes("pharmacy")) && Object.hasOwn(data, "claim");
    const shouldShowPriorAuthorizations = (hasNoFilter || claimsType.includes("prior_authorization")) && Object.hasOwn(data, "case");
    const shouldShowDentalClaims = isDentalUser && (hasNoFilter || claimsType.includes("dental")) && dataIsMedicalClaimsType && data.medical_claim.type === "dental";
    const shouldShowVisionClaims = isVisionUser && (hasNoFilter || claimsType.includes("vision")) && dataIsMedicalClaimsType && data.medical_claim.type === "vision";
    const shouldShowMedicalClaims = hasMedicalBenefits && (hasNoFilter || claimsType.includes("medical")) && dataIsMedicalClaimsType && (data.medical_claim.type !== "dental" && data.medical_claim.type !== "vision" );

    return shouldShowPharmacy || shouldShowPriorAuthorizations || shouldShowDentalClaims || shouldShowVisionClaims || shouldShowMedicalClaims;
  });

  if (dataResults.length > 0) {
    // @ts-ignore
    combinedActivity = dataResults.map((data) => {
      if (Object.hasOwn(data, "claim")) {
        return ({
          activity_date: data.claim.date_of_service,
          title: data.claim.drug_name,
          description:
            <Body>{translate(`${TRANSLATION_PATH}.yourPharmacyClaim`)}
              <Semibold>{ formatDollars(data.claim.patient_pay_amount) }</Semibold>.
            </Body>,
          claim: data.claim,
          link: () => navigate(`/claims-history/pharmacy/${data.claim.id}/${data.claim.person_code}`, { state: { previousPage: "home" } }),
        });
      } else if (Object.hasOwn(data, "case")) {
        return ({
          activity_date: data.case.created_at,
          title: data.case.drug_name,
          description:
            <Body>{translate(`${TRANSLATION_PATH}.${data.case_outcome?.outcome ? "yourPriorAuthorization" : "yourInProgressPA"}`)}
              <Semibold>{translate(`${TRANSLATION_PATH}.${data.case_outcome?.outcome || "in_progress"}`)} </Semibold>
            </Body>,
          pa_case: data.case,
          link: () => navigate(`/prior-authorization/${data.case.case_id}`, { state: { previousPage: "home" } }),
        });
      } else {
        let medicalClaimType = "medical";
        if (data.medical_claim.type === "dental" || data.medical_claim.type === "vision") {
          medicalClaimType = data.medical_claim.type;
        }
        return ({
          activity_date: data.medical_claim.received_at,
          title: getClaimProviderName(
            data.medical_claim?.billing_provider_first_name,
            data.medical_claim?.billing_provider_last_name_or_organizational_name,
          ),
          description:
            <Body>{translate(`${TRANSLATION_PATH}.yourClaim`)}
              <Semibold>{translate(`${TRANSLATION_PATH}.${data.medical_claim.status}`)} </Semibold>
              {data.medical_claim.status === "denied" && translate(`${TRANSLATION_PATH}.viewDeniedClaim`)}
            </Body>,
          medical_claim: data.medical_claim,
          link: () => navigate(`/claims-history/${medicalClaimType}/${data.medical_claim.id}/${data.medical_claim.person_code}`, { state: { previousPage: "home" } }),
        });
      }
    });
  }

  combinedActivity.sort((a, b) => {
    return Date.parse(b.activity_date) - Date.parse(a.activity_date);
  });

  combinedActivity.length = 8;

  const handleFilterSelection = (newValue: RecentActivityTypes[] | null | undefined) => {
    setClaimsType(newValue);
  };

  const handleBadgeClose = (index: number) => {
    setClaimsType(claimsType.toSpliced(index, 1));
  };

  return (
    <Stack spacing="large">
      <Stack spacing="medium">
        <Stack spacing="small">
          <Subtitle testId="recent-activity-title">
            {translate(`${TRANSLATION_PATH}.recentActivityTitle`)}
          </Subtitle>
          <Body>
            {translate(`${TRANSLATION_PATH}.recentActivitySubtitle`)}
          </Body>
        </Stack>
        <Stack spacing="small">
          <StackOrShelf>
            <Shelf spacing="none">
              <Icon name="filter" color={Color.Turqoise["500"]} />
              <TextMultiSelectDropdown placeholder="Filter" hideLabel onChange={handleFilterSelection} value={claimsType}>
                {
                  availableFilters.map(({ type, value }) => <TextMultiSelectDropdown.Option key={type} value={type}>{value}</TextMultiSelectDropdown.Option>)
                }
              </TextMultiSelectDropdown>
            </Shelf>
            <Shelf.Spacer />
          </StackOrShelf>
          <Shelf spacing="small">
            {!!claimsType && claimsType.map((activity, index) => <ActivityBadge key={activity} activity={activity} index={index} handleBadgeClose={handleBadgeClose} />)}
            <Shelf.Spacer />
          </Shelf>
        </Stack>
      </Stack>
      <Stack>
        {
          combinedActivity.map((activity, index) => <ActivityCard key={activity.activity_date + index} activity={activity} />)
        }
      </Stack>
    </Stack>
  );
};
